/**
 * Analytics layer — env-driven. Zero-cost when IDs are not set.
 *
 * Providers:
 *  - Google Analytics 4  (REACT_APP_GA4_MEASUREMENT_ID)
 *  - Meta Pixel          (REACT_APP_META_PIXEL_ID)
 *  - Google Search Console verification meta (REACT_APP_GSC_VERIFICATION)
 *
 * Per product decision: cookie notice is minimal & informational, tracking fires
 * immediately on first paint (no consent gate). If we later switch to strict
 * GDPR consent, gate `init()` behind the acceptance flag.
 */

const GA4_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || "";
const META_ID = process.env.REACT_APP_META_PIXEL_ID || "";
const GSC_TOKEN = process.env.REACT_APP_GSC_VERIFICATION || "";

let initialized = false;

function injectScript(src, id) {
  if (document.getElementById(id)) return;
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  s.id = id;
  document.head.appendChild(s);
}

function injectInline(code, id) {
  if (document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  s.text = code;
  document.head.appendChild(s);
}

function injectMeta(name, content, id) {
  if (!content || document.getElementById(id)) return;
  const m = document.createElement("meta");
  m.id = id;
  m.name = name;
  m.content = content;
  document.head.appendChild(m);
}

export function initAnalytics() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  // Google Search Console verification
  injectMeta("google-site-verification", GSC_TOKEN, "gsc-verification");

  // GA4
  if (GA4_ID) {
    injectScript(`https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`, "ga4-loader");
    injectInline(
      `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js',new Date());gtag('config','${GA4_ID}',{send_page_view:false});`,
      "ga4-init"
    );
  }

  // Meta Pixel
  if (META_ID) {
    injectInline(
      `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${META_ID}');`,
      "meta-pixel-init"
    );
  }
}

export function trackPageView(path) {
  if (typeof window === "undefined") return;
  if (GA4_ID && window.gtag) {
    window.gtag("event", "page_view", {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
    });
  }
  if (META_ID && window.fbq) {
    window.fbq("track", "PageView");
  }
}

/**
 * Fire a custom event across all configured providers.
 * @param {string} name   canonical event name (snake_case)
 * @param {object} params optional properties
 */
export function trackEvent(name, params = {}) {
  if (typeof window === "undefined") return;
  try {
    if (GA4_ID && window.gtag) window.gtag("event", name, params);
    if (META_ID && window.fbq) {
      // Map high-value events to Meta standard events, others -> trackCustom
      const standard = {
        newsletter_signup: "Lead",
        book_interest: "Lead",
        contact_submit: "Contact",
        builders_circle_waitlist: "Lead",
        purchase: "Purchase",
      };
      const std = standard[name];
      if (std) window.fbq("track", std, params);
      else window.fbq("trackCustom", name, params);
    }
  } catch (_) {
    /* never break UX on analytics failure */
  }
}

// Semantic helpers — call sites read cleanly.
export const track = {
  newsletter: (source_page, tags) =>
    trackEvent(
      tags?.includes("Book Interest")
        ? "book_interest"
        : tags?.includes("Builders Circle")
        ? "builders_circle_waitlist"
        : "newsletter_signup",
      { source_page, tags }
    ),
  contact: (source_page) => trackEvent("contact_submit", { source_page }),
  cta: (label, location) => trackEvent("cta_click", { label, location }),
  download: (asset) => trackEvent("download", { asset }),
};

export const analyticsConfig = {
  enabled: Boolean(GA4_ID || META_ID),
  ga4: Boolean(GA4_ID),
  meta: Boolean(META_ID),
};
